"use client";

import React from "react";
import { Check, Info } from "lucide-react";
import { Link } from "@/components/common/Link";
import { Card, CardHeader, CardTitle, CardDescription, CardContent, CardFooter } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Separator } from "@/components/ui/separator";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
const plans = [{
  id: "direct-routing",
  name: "Direct Routing",
  description: "Máxima flexibilidad y control total de la infraestructura.",
  target: "Empresas con infraestructuras complejas o legadas.",
  priceLabel: "Personalizado",
  popular: false
}, {
  id: "operator-connect",
  name: "Operator Connect",
  description: "Simplicidad gestionada directamente desde Microsoft Teams.",
  target: "Empresas que buscan rapidez y facilidad de gestión.",
  priceLabel: "Suscripción o Compra",
  popular: true
}, {
  id: "hybrid-solution",
  name: "Solución Híbrida",
  description: "Lo mejor de ambos mundos para despliegues globales.",
  target: "Ideal para:\nMigración paulatina o cuando se requieren servicios especiales como Call Center avanzado, campañas de marcación, encuestas , solo se migra una parte a Teams.",
  priceLabel: "Consultar",
  popular: false
}];
const planFeatures = [{
  planId: "direct-routing",
  feature: "Complejidad de Configuración: Alta"
}, {
  planId: "direct-routing",
  feature: "Estructura de Costos: Inversión Inicial"
}, {
  planId: "direct-routing",
  feature: "Requisitos de Red: SBC Propios"
}, {
  planId: "direct-routing",
  feature: "Soporte de Failover: Configurable"
}, {
  planId: "direct-routing",
  feature: "Escalabilidad: Manual"
}, {
  planId: "direct-routing",
  feature: "Nivel de Soporte: Interno/Partner"
}, {
  planId: "operator-connect",
  feature: "Complejidad de Configuración: Baja"
}, {
  planId: "operator-connect",
  feature: "Estructura de Costos: Pago por Uso"
}, {
  planId: "operator-connect",
  feature: "Requisitos de Red: Gestionado"
}, {
  planId: "operator-connect",
  feature: "Soporte de Failover: Nativo"
}, {
  planId: "operator-connect",
  feature: "Escalabilidad: Instantánea"
}, {
  planId: "operator-connect",
  feature: "Nivel de Soporte: Operador Directo"
}, {
  planId: "hybrid-solution",
  feature: "Complejidad de Configuración: Media"
}, {
  planId: "hybrid-solution",
  feature: "Estructura de Costos: Mixta"
}, {
  planId: "hybrid-solution",
  feature: "Requisitos de Red: Adaptable"
}, {
  planId: "hybrid-solution",
  feature: "Soporte de Failover: Redundante"
}, {
  planId: "hybrid-solution",
  feature: "Escalabilidad: Flexible"
}, {
  planId: "hybrid-solution",
  feature: "Nivel de Soporte: Premium 24/7"
}];
export default function DirectRoutingVsOperatorConnectComparison() {
  return <section data-section-id="259528" id="direct-routing-vs-operator-connect-comparison" className="bg-background text-foreground py-16 md:py-24">
      <div className="container mx-auto px-4 md:px-6">
        <div className="mb-12 text-center max-w-3xl mx-auto">
          <h2 className="text-3xl md:text-4xl font-default font-bold text-foreground mb-4">
            Direct Routing vs Operator Connect
          </h2>
          <p className="text-muted-foreground text-base font-default">
            Compare las dos formas principales de habilitar llamadas de voz en Microsoft Teams. Analizamos la complejidad, los costos y el soporte para ayudarle a elegir la mejor opción para su infraestructura omnicanal.
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 lg:gap-8">
          {plans.map((plan, i) => <Card key={plan.id} data-index={i} className={`relative flex flex-col h-full rounded-md border-border transition-all duration-300 hover:-translate-y-2 hover:shadow-xl ${plan.popular ? "bg-accent border-t-4 border-t-primary shadow-md" : "bg-card shadow-sm"}`}>
              {plan.popular && <div className="absolute -top-3 left-1/2 -translate-x-1/2">
                  <Badge variant="default" className="px-3 py-1 text-sm bg-primary text-primary-foreground">
                    Recomendado
                  </Badge>
                </div>}

              <CardHeader className="pb-4">
                <CardTitle className="text-xl font-default font-bold text-foreground">
                  {plan.name}
                </CardTitle>
                <CardDescription className="min-h-[40px] text-sm font-default">
                  {plan.description}
                </CardDescription>
              </CardHeader>

              <CardContent className="flex-grow flex flex-col gap-4 pb-6">
                <div className="flex items-center justify-between">
                  <span className="text-2xl font-default font-bold text-foreground">
                    {plan.priceLabel}
                  </span>
                  <TooltipProvider>
                    <Tooltip>
                      <TooltipTrigger asChild>
                        <Button variant="ghost" size="icon" className="text-muted-foreground hover:text-foreground">
                          <Info className="size-5" />
                          <span className="sr-only">Información de Costos</span>
                        </Button>
                      </TooltipTrigger>
                      <TooltipContent>
                        <p className="max-w-xs font-default text-sm">
                          Los costos varían según el volumen de usuarios y la región. Solicite una cotización para su caso específico.
                        </p>
                      </TooltipContent>
                    </Tooltip>
                  </TooltipProvider>
                </div>

                <div className="bg-muted/50 rounded-md p-3 text-sm text-foreground font-default">
                  <span className="font-semibold block mb-1">Ideal para:</span>
                  {plan.target}
                </div>

                <Separator className="my-2" />

                <ul className="space-y-3 flex-grow">
                  {planFeatures.filter(f => f.planId === plan.id).map((feature, index) => <li key={index} data-index={index} className="flex items-start gap-3 text-sm text-muted-foreground font-default">
                        <Check className="size-5 text-primary shrink-0 mt-0.5" />
                        <span>{feature.feature}</span>
                      </li>)}
                </ul>
              </CardContent>

              <CardFooter className="pt-0 mt-auto">
                <Button asChild variant={plan.popular ? "default" : "outline"} className={`w-full font-default ${plan.popular ? "bg-yellow-400 hover:bg-yellow-500 text-black" : ""}`}>
                  <Link to="/OmnichannelPage">Saber más</Link>
                </Button>
              </CardFooter>
            </Card>)}
        </div>

        <div className="mt-16 bg-muted/30 rounded-md p-6 md:p-8 text-center max-w-4xl mx-auto border border-border">
          <h3 className="text-xl font-default font-semibold text-foreground mb-3">
            ¿Necesita una solución a medida?
          </h3>
          <p className="text-muted-foreground mb-6 font-default text-base">
            Nuestros expertos en telefonía IP pueden diseñar una arquitectura que combine la potencia de Direct Routing con la agilidad de Operator Connect para su empresa.
          </p>
          <Button asChild variant="secondary" className="font-default">
            <Link to="/HomePage">Contactar con un experto</Link>
          </Button>
        </div>
      </div>
    </section>;
}